/**
 * Data Access Layer for Staff Shifts & Assignments
 *
 * Shifts are coverage slots (one per role-unit) generated for a programme; the
 * greedy generation/assignment engine lands in a later phase. This module
 * provides shift persistence, the joined read used by the timetable, and the
 * manual-edit helpers (assign / unassign / confirm) that let an organiser
 * hand-tune the schedule. Manual assignments set is_locked so a future engine
 * re-run won't overwrite them.
 */

import { getClient } from './client.js';
import { secureToken } from './secure-token.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import { listHappeningRoleRequirementsBatch } from './happening-role-requirements.js';
import {
  buildShiftPlan,
  assignShiftsGreedy,
  type ShiftSpec,
  type EnginePerson,
  type EngineShift,
  type ExistingAssignment,
} from './staffing-engine.js';
import type {
  StaffShift,
  StaffShiftSource,
  StaffAssignment,
  StaffAssignmentStatus,
  StaffShiftWithAssignment,
} from './types.js';

// ============================================
// Shifts
// ============================================

export async function listProgramShifts(
  programId: string,
): Promise<StaffShiftWithAssignment[]> {
  const client = getClient();

  const { data: shiftRows, error: shiftError } = await client
    .from(TABLES.staff_shifts)
    .select('*')
    .eq('program_id', programId)
    .order('start_time');
  if (shiftError) throw shiftError;

  const shifts = (shiftRows || []) as StaffShift[];
  if (shifts.length === 0) return [];

  const { data: assignmentRows, error: assignmentError } = await client
    .from(TABLES.staff_assignments)
    .select('*, person:person_id (name)')
    .in('shift_id', shifts.map((s) => s.id));
  if (assignmentError) throw assignmentError;

  const byShift = new Map<string, { assignment: StaffAssignment; person_name?: string }>();
  for (const row of (assignmentRows || []) as any[]) {
    const { person, ...assignment } = row;
    byShift.set(assignment.shift_id, {
      assignment: assignment as StaffAssignment,
      person_name: person?.name ?? undefined,
    });
  }

  return shifts.map((shift) => {
    const hit = byShift.get(shift.id);
    return {
      ...shift,
      assignment: hit?.assignment ?? null,
      person_name: hit?.person_name,
    };
  });
}

export async function createShift(
  input: Omit<StaffShift, 'id' | 'account_id' | 'source' | 'created_at' | 'updated_at'> & {
    source?: StaffShiftSource;
  },
): Promise<StaffShift> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('createShift: no account context set');

  // Direct creation defaults to a manual shift (preserved across regeneration).
  const { data, error } = await getClient()
    .from(TABLES.staff_shifts)
    .insert({ ...input, account_id: accountId, source: input.source ?? 'manual' })
    .select()
    .single();

  if (error) throw error;
  return data as StaffShift;
}

export async function deleteShift(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.staff_shifts).delete().eq('id', id);
  if (error) throw error;
}

/**
 * Update a shift's times and/or source. Resizing a generated shift should pass
 * source: 'manual' so the change survives the next regeneration.
 */
export async function updateShift(
  id: string,
  patch: { start_time?: string; end_time?: string; source?: StaffShiftSource },
): Promise<StaffShift> {
  const { data, error } = await getClient()
    .from(TABLES.staff_shifts)
    .update(patch)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StaffShift;
}

export async function deleteProgramShifts(programId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.staff_shifts)
    .delete()
    .eq('program_id', programId);
  if (error) throw error;
}

// ============================================
// Assignments (manual edits)
// ============================================

/**
 * Assign a person to a shift. Replaces any existing assignment on that shift.
 * Pass lock = true (the default for manual edits) to pin the choice so a future
 * engine re-run won't overwrite it.
 */
export async function assignPersonToShift(
  shiftId: string,
  personId: string,
  options?: { lock?: boolean; status?: StaffAssignmentStatus },
): Promise<StaffAssignment> {
  const client = getClient();

  const { error: clearError } = await client
    .from(TABLES.staff_assignments)
    .delete()
    .eq('shift_id', shiftId);
  if (clearError) throw clearError;

  const { data, error } = await client
    .from(TABLES.staff_assignments)
    .insert({
      shift_id: shiftId,
      person_id: personId,
      status: options?.status ?? 'proposed',
      is_locked: options?.lock ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as StaffAssignment;
}

export async function unassignShift(shiftId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.staff_assignments)
    .delete()
    .eq('shift_id', shiftId);
  if (error) throw error;
}

// ============================================
// Engine: shift generation + auto-assignment
// ============================================

const shiftKey = (placeId: string, roleId: string, start: string, end: string) =>
  `${placeId}|${roleId}|${Date.parse(start)}|${Date.parse(end)}`;

export interface GenerateShiftsResult {
  created: number;
  deleted: number;
  kept: number;
}

/**
 * Regenerate a programme's staff shifts from its happenings' role requirements.
 * Diffs against existing shifts: matching shifts are kept (so their assignments
 * survive), obsolete ones are deleted unless they carry a locked assignment, and
 * newly-required ones are inserted.
 */
export async function generateShifts(
  programId: string,
  options?: {
    minShiftMinutes?: number;
    maxShiftMinutes?: number;
    leadInMinutes?: number;
    leadOutMinutes?: number;
  },
): Promise<GenerateShiftsResult> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('generateShifts: no account context set');

  const client = getClient();

  const { data: happeningRows, error: hErr } = await client
    .from(TABLES.happenings)
    .select('id, place_id, start_time, end_time')
    .eq('program_id', programId);
  if (hErr) throw hErr;

  const happenings = (happeningRows ?? []) as Array<{
    id: string;
    place_id: string | null;
    start_time: string | null;
    end_time: string | null;
  }>;

  const requirements = await listHappeningRoleRequirementsBatch(happenings.map((h) => h.id));
  const requirementsByHappening: Record<string, Array<{ role_id: string; count: number }>> = {};
  for (const [hid, reqs] of Object.entries(requirements)) {
    requirementsByHappening[hid] = reqs.map((r) => ({ role_id: r.role_id, count: r.count }));
  }

  const desired: ShiftSpec[] = buildShiftPlan({
    happenings,
    requirements: requirementsByHappening,
    minShiftMinutes: options?.minShiftMinutes ?? 180,
    maxShiftMinutes: options?.maxShiftMinutes ?? 300,
    leadInMinutes: options?.leadInMinutes ?? 0,
    leadOutMinutes: options?.leadOutMinutes ?? 0,
  });

  // Diff only against generated shifts — manual shifts are never touched here.
  const existing = await listProgramShifts(programId);
  const generated = existing.filter((s) => s.source !== 'manual');
  const existingByKey = new Map<string, StaffShiftWithAssignment>();
  for (const s of generated) existingByKey.set(shiftKey(s.place_id, s.role_id, s.start_time, s.end_time), s);

  const desiredKeys = new Set(desired.map((d) => shiftKey(d.place_id, d.role_id, d.start, d.end)));

  // Delete obsolete generated shifts unless they hold a locked assignment.
  const toDelete = generated.filter(
    (s) => !desiredKeys.has(shiftKey(s.place_id, s.role_id, s.start_time, s.end_time)) && !s.assignment?.is_locked,
  );
  if (toDelete.length > 0) {
    const { error } = await client
      .from(TABLES.staff_shifts)
      .delete()
      .in('id', toDelete.map((s) => s.id));
    if (error) throw error;
  }

  // Insert generated shifts that don't already exist.
  const toInsert = desired
    .filter((d) => !existingByKey.has(shiftKey(d.place_id, d.role_id, d.start, d.end)))
    .map((d) => ({
      account_id: accountId,
      program_id: programId,
      place_id: d.place_id,
      role_id: d.role_id,
      start_time: d.start,
      end_time: d.end,
      source: 'generated' as StaffShiftSource,
    }));
  if (toInsert.length > 0) {
    const { error } = await client.from(TABLES.staff_shifts).insert(toInsert);
    if (error) throw error;
  }

  return {
    created: toInsert.length,
    deleted: toDelete.length,
    kept: generated.length - toDelete.length,
  };
}

export interface AutoAssignResult {
  assigned: number;
  unfilled: number;
  warnings: Array<{ shiftId: string; message: string }>;
}

/**
 * Greedily fill a programme's unlocked shifts from the eligible staff pool.
 * Clears prior unlocked (proposed) assignments and recomputes them; locked
 * assignments are preserved and count toward each person's load.
 */
export async function autoAssignStaff(
  programId: string,
  options?: { continuityGapMinutes?: number; maxShiftsPerPerson?: number },
): Promise<AutoAssignResult> {
  const accountId = getCurrentAccountId();
  const client = getClient();

  const shiftsWithAssignment = await listProgramShifts(programId);
  if (shiftsWithAssignment.length === 0) {
    return { assigned: 0, unfilled: 0, warnings: [] };
  }

  const engineShifts: EngineShift[] = shiftsWithAssignment.map((s) => ({
    id: s.id,
    place_id: s.place_id,
    role_id: s.role_id,
    start: s.start_time,
    end: s.end_time,
  }));
  const existingAssignments: ExistingAssignment[] = shiftsWithAssignment
    .filter((s) => s.assignment)
    .map((s) => ({ shiftId: s.id, personId: s.assignment!.person_id, locked: s.assignment!.is_locked }));

  // Eligible pool: staff/support people, their role qualifications, availability
  // (program-scoped or general), and scheduling constraints from metadata.
  let peopleQuery = client
    .from(TABLES.people)
    .select('id, metadata')
    .in('type', ['staff', 'support']);
  if (accountId) peopleQuery = peopleQuery.eq('account_id', accountId);
  const { data: peopleRows, error: pErr } = await peopleQuery;
  if (pErr) throw pErr;

  const personIds = (peopleRows ?? []).map((p: any) => p.id);
  if (personIds.length === 0) {
    return { assigned: 0, unfilled: engineShifts.filter((s) => !existingAssignments.some((a) => a.shiftId === s.id && a.locked)).length, warnings: [] };
  }

  const { data: roleRows, error: rErr } = await client
    .from(TABLES.person_roles)
    .select('person_id, role_id')
    .in('person_id', personIds);
  if (rErr) throw rErr;

  const rolesByPerson = new Map<string, string[]>();
  for (const row of (roleRows ?? []) as Array<{ person_id: string; role_id: string }>) {
    (rolesByPerson.get(row.person_id) ?? rolesByPerson.set(row.person_id, []).get(row.person_id)!).push(row.role_id);
  }

  const { data: availRows, error: aErr } = await client
    .from(TABLES.staff_availability)
    .select('person_id, program_id, start_time, end_time')
    .in('person_id', personIds)
    .or(`program_id.eq.${programId},program_id.is.null`);
  if (aErr) throw aErr;

  const availByPerson = new Map<string, Array<{ start: string; end: string }>>();
  for (const row of (availRows ?? []) as Array<{ person_id: string; start_time: string; end_time: string }>) {
    (availByPerson.get(row.person_id) ?? availByPerson.set(row.person_id, []).get(row.person_id)!).push({
      start: row.start_time,
      end: row.end_time,
    });
  }

  const people: EnginePerson[] = (peopleRows ?? []).map((p: any) => {
    const staffing = (p.metadata && typeof p.metadata === 'object' ? p.metadata.staffing : null) ?? null;
    return {
      id: p.id,
      roleIds: rolesByPerson.get(p.id) ?? [],
      availability: availByPerson.get(p.id) ?? [],
      maxConsecutiveHours: staffing?.max_consecutive_hours ?? null,
      maxDailyHours: staffing?.max_daily_hours ?? null,
      allowedPlaceIds: staffing?.allowed_place_ids ?? null,
    };
  });

  const result = assignShiftsGreedy(engineShifts, people, existingAssignments, options);

  // Clear prior unlocked assignments, then insert the fresh proposals.
  const shiftIds = shiftsWithAssignment.map((s) => s.id);
  const { error: clearErr } = await client
    .from(TABLES.staff_assignments)
    .delete()
    .in('shift_id', shiftIds)
    .eq('is_locked', false);
  if (clearErr) throw clearErr;

  if (result.assignments.length > 0) {
    const rows = result.assignments.map((a) => ({
      shift_id: a.shiftId,
      person_id: a.personId,
      status: 'proposed' as StaffAssignmentStatus,
      is_locked: false,
    }));
    const { error: insErr } = await client.from(TABLES.staff_assignments).insert(rows);
    if (insErr) throw insErr;
  }

  return {
    assigned: result.assignments.length,
    unfilled: result.unfilled.length,
    warnings: result.warnings,
  };
}

// ============================================
// Public read-only timetable views (no-auth, token-gated)
// ============================================

const randomTimetableToken = () => secureToken();

const timesOverlap = (aStart: string, aEnd: string, bStart: string, bEnd: string) =>
  Date.parse(aStart) < Date.parse(bEnd) && Date.parse(aEnd) > Date.parse(bStart);

export interface TimetablePerformance {
  id: string;
  title: string;
  place_id: string;
  start_time: string | null;
  end_time: string | null;
  artists: string[];
}

export interface StaffScheduleShift {
  id: string;
  program_id: string;
  program_name: string;
  place_id: string;
  place_name: string;
  role_id: string;
  role_name: string;
  start_time: string;
  end_time: string;
  status: StaffAssignmentStatus;
  /** Programmed performances overlapping this shift, only when the shift's programme has revealed its lineup. */
  performances?: TimetablePerformance[];
}

export interface StaffScheduleProgramme {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  location: string | null;
  contact: StaffingContact | null;
}

export interface StaffSchedule {
  person: { id: string; name: string; slug: string };
  /** The distinct programmes this person has shifts in, each with its staffing contact. */
  programmes: StaffScheduleProgramme[];
  shifts: StaffScheduleShift[];
}

type ParticipantRow = {
  role: string;
  sort_order: number | null;
  people: { name: string | null } | null;
};

type HappeningRow = {
  id: string;
  title: string | null;
  place_id: string | null;
  program_id: string | null;
  start_time: string | null;
  end_time: string | null;
  happening_participants?: ParticipantRow[] | null;
};

const HAPPENING_SELECT =
  'id, title, place_id, program_id, start_time, end_time, happening_participants(role, sort_order, people(name))';

function artistNames(row: HappeningRow): string[] {
  return (row.happening_participants ?? [])
    .filter((p) => p.role === 'artist' && p.people?.name)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((p) => p.people!.name!);
}

function toPerformance(row: HappeningRow): TimetablePerformance {
  return {
    id: row.id,
    title: row.title ?? 'Untitled',
    place_id: row.place_id!,
    start_time: row.start_time,
    end_time: row.end_time,
    artists: artistNames(row),
  };
}

/** Whether a programme has been flipped to reveal its lineup to staff views. */
const isRevealed = (settings: unknown): boolean =>
  Boolean(settings && typeof settings === 'object' && (settings as Record<string, unknown>).lineup_revealed);

export interface StaffingContact {
  name: string;
  email: string;
  phone: string;
}

/** Free-text festival location shown in the timetable header (settings.location). */
const readLocation = (settings: unknown): string | null => {
  if (!settings || typeof settings !== 'object') return null;
  const loc = (settings as Record<string, unknown>).location;
  return typeof loc === 'string' && loc.trim() ? loc.trim() : null;
};

/** The person who built the shifts, shown on staff-facing views so they know who to reach. */
const readContact = (settings: unknown): StaffingContact | null => {
  if (!settings || typeof settings !== 'object') return null;
  const s = settings as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const name = str(s.staffing_contact_name);
  const email = str(s.staffing_contact_email);
  const phone = str(s.staffing_contact_phone);
  if (!name && !email && !phone) return null;
  return { name, email, phone };
};

/**
 * Read a single staff member's schedule across all programmes, identified by
 * their invite token (the no-auth credential, same model as the artist invite
 * link). Performances are attached only for shifts whose programme has revealed
 * its lineup; otherwise the lineup stays secret and no artist data is returned.
 */
export async function getStaffScheduleByToken(token: string): Promise<StaffSchedule | null> {
  const normalized = token?.trim();
  if (!normalized) return null;

  const client = getClient();

  const { data: peopleRows, error: personErr } = await client
    .from(TABLES.people)
    .select('id, name, slug')
    .eq('profile_completion_token', normalized)
    .limit(1);
  if (personErr) throw personErr;
  const person = (peopleRows ?? [])[0] as { id: string; name: string | null; slug: string } | undefined;
  if (!person) return null;

  const { data: assignmentRows, error: assignErr } = await client
    .from(TABLES.staff_assignments)
    .select('shift_id, status')
    .eq('person_id', person.id);
  if (assignErr) throw assignErr;

  const statusByShift = new Map<string, StaffAssignmentStatus>();
  for (const row of (assignmentRows ?? []) as Array<{ shift_id: string; status: StaffAssignmentStatus }>) {
    statusByShift.set(row.shift_id, row.status);
  }
  const shiftIds = [...statusByShift.keys()];

  const result: StaffSchedule = {
    person: { id: person.id, name: person.name ?? '', slug: person.slug },
    programmes: [],
    shifts: [],
  };
  if (shiftIds.length === 0) return result;

  const { data: shiftRows, error: shiftErr } = await client
    .from(TABLES.staff_shifts)
    .select('*')
    .in('id', shiftIds)
    .order('start_time');
  if (shiftErr) throw shiftErr;
  const shifts = (shiftRows ?? []) as StaffShift[];

  const programIds = [...new Set(shifts.map((s) => s.program_id))];
  const placeIds = [...new Set(shifts.map((s) => s.place_id))];
  const roleIds = [...new Set(shifts.map((s) => s.role_id))];

  const [programs, places, roles] = await Promise.all([
    client.from(TABLES.programs).select('id, name, start_time, end_time, settings').in('id', programIds),
    client.from(TABLES.places).select('id, name').in('id', placeIds),
    client.from(TABLES.staff_roles).select('id, name').in('id', roleIds),
  ]);
  if (programs.error) throw programs.error;
  if (places.error) throw places.error;
  if (roles.error) throw roles.error;

  const programById = new Map(
    ((programs.data ?? []) as Array<{ id: string; name: string; start_time: string; end_time: string; settings: unknown }>).map((p) => [p.id, p]),
  );
  const placeNameById = new Map(((places.data ?? []) as Array<{ id: string; name: string }>).map((p) => [p.id, p.name]));
  const roleNameById = new Map(((roles.data ?? []) as Array<{ id: string; name: string }>).map((r) => [r.id, r.name]));

  result.programmes = programIds
    .map((id) => programById.get(id))
    .filter((p): p is { id: string; name: string; start_time: string; end_time: string; settings: unknown } => Boolean(p))
    .map((p) => ({
      id: p.id,
      name: p.name,
      start_time: p.start_time,
      end_time: p.end_time,
      location: readLocation(p.settings),
      contact: readContact(p.settings),
    }));

  const revealedProgramIds = programIds.filter((id) => isRevealed(programById.get(id)?.settings));
  let revealedHappenings: HappeningRow[] = [];
  if (revealedProgramIds.length > 0) {
    const { data: happeningRows, error: hErr } = await client
      .from(TABLES.happenings)
      .select(HAPPENING_SELECT)
      .in('program_id', revealedProgramIds);
    if (hErr) throw hErr;
    revealedHappenings = (happeningRows ?? []) as unknown as HappeningRow[];
  }

  result.shifts = shifts.map((shift) => {
    const program = programById.get(shift.program_id);
    const out: StaffScheduleShift = {
      id: shift.id,
      program_id: shift.program_id,
      program_name: program?.name ?? '',
      place_id: shift.place_id,
      place_name: placeNameById.get(shift.place_id) ?? '',
      role_id: shift.role_id,
      role_name: roleNameById.get(shift.role_id) ?? '',
      start_time: shift.start_time,
      end_time: shift.end_time,
      status: statusByShift.get(shift.id) ?? 'proposed',
    };
    if (isRevealed(program?.settings)) {
      out.performances = revealedHappenings
        .filter(
          (h) =>
            h.program_id === shift.program_id &&
            h.place_id === shift.place_id &&
            h.start_time &&
            timesOverlap(h.start_time, h.end_time ?? h.start_time, shift.start_time, shift.end_time),
        )
        .map(toPerformance);
    }
    return out;
  });

  return result;
}

export interface ProgramTimetable {
  program: {
    id: string;
    name: string;
    start_time: string;
    end_time: string;
    location: string | null;
    lineup_revealed: boolean;
    contact: StaffingContact | null;
  };
  places: Array<{ id: string; name: string }>;
  roles: Array<{ id: string; name: string }>;
  shifts: StaffShiftWithAssignment[];
  /** Programmed performances, only populated when the programme's lineup is revealed. */
  performances: TimetablePerformance[];
}

/**
 * Read the full read-only staffing grid for one programme (all stages, roles and
 * staff), gated by the programme's timetable token. Performances are included
 * only when the lineup has been revealed.
 */
export async function getProgramTimetableByToken(
  programSlug: string,
  token: string,
): Promise<ProgramTimetable | null> {
  const normalized = token?.trim();
  if (!normalized) return null;

  const client = getClient();

  const { data: programRows, error: programErr } = await client
    .from(TABLES.programs)
    .select('id, name, start_time, end_time, settings')
    .eq('slug', programSlug)
    .limit(1);
  if (programErr) throw programErr;
  const program = (programRows ?? [])[0] as
    | { id: string; name: string; start_time: string; end_time: string; settings: unknown }
    | undefined;
  if (!program) return null;

  const settings = (program.settings && typeof program.settings === 'object'
    ? (program.settings as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  if (settings.timetable_token !== normalized) return null;

  const revealed = isRevealed(settings);
  const shifts = await listProgramShifts(program.id);

  const placeIds = [...new Set(shifts.map((s) => s.place_id))];
  const roleIds = [...new Set(shifts.map((s) => s.role_id))];

  const [places, roles] = await Promise.all([
    placeIds.length
      ? client.from(TABLES.places).select('id, name').in('id', placeIds)
      : Promise.resolve({ data: [], error: null }),
    roleIds.length
      ? client.from(TABLES.staff_roles).select('id, name').in('id', roleIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (places.error) throw places.error;
  if (roles.error) throw roles.error;

  let performances: TimetablePerformance[] = [];
  if (revealed) {
    const { data: happeningRows, error: hErr } = await client
      .from(TABLES.happenings)
      .select(HAPPENING_SELECT)
      .eq('program_id', program.id);
    if (hErr) throw hErr;
    performances = ((happeningRows ?? []) as unknown as HappeningRow[])
      .filter((h) => h.place_id && h.start_time)
      .map(toPerformance);
  }

  return {
    program: {
      id: program.id,
      name: program.name,
      start_time: program.start_time,
      end_time: program.end_time,
      location: readLocation(settings),
      lineup_revealed: revealed,
      contact: readContact(settings),
    },
    places: (places.data ?? []) as Array<{ id: string; name: string }>,
    roles: (roles.data ?? []) as Array<{ id: string; name: string }>,
    shifts,
    performances,
  };
}

/**
 * Return the programme's global timetable token, generating and persisting one
 * on first use. Called when an organiser copies the global timetable link.
 */
export async function ensureProgramTimetableToken(programId: string): Promise<string> {
  const client = getClient();

  const { data: row, error } = await client
    .from(TABLES.programs)
    .select('settings')
    .eq('id', programId)
    .single();
  if (error) throw error;

  const settings = (row?.settings && typeof row.settings === 'object'
    ? (row.settings as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const existing = settings.timetable_token;
  if (typeof existing === 'string' && existing) return existing;

  const token = randomTimetableToken();
  const { error: updateErr } = await client
    .from(TABLES.programs)
    .update({ settings: { ...settings, timetable_token: token } })
    .eq('id', programId);
  if (updateErr) throw updateErr;

  return token;
}

/**
 * Confirm all assignments for a programme's shifts: mark them 'confirmed' AND
 * lock them, so a future engine re-run (auto-assign) won't overwrite them.
 * Locking is what actually protects the roster — status is the human-facing
 * lifecycle flag. Reversible per-shift via reopenShiftAssignment or in bulk via
 * reopenAssignments.
 */
export async function confirmAssignments(programId: string): Promise<void> {
  const client = getClient();

  const { data: shiftRows, error: shiftError } = await client
    .from(TABLES.staff_shifts)
    .select('id')
    .eq('program_id', programId);
  if (shiftError) throw shiftError;

  const shiftIds = (shiftRows || []).map((r: any) => r.id);
  if (shiftIds.length === 0) return;

  const { error } = await client
    .from(TABLES.staff_assignments)
    .update({ status: 'confirmed', is_locked: true })
    .in('shift_id', shiftIds);
  if (error) throw error;
}

/**
 * Reopen a single shift's assignment: set it back to 'proposed' and unlock it,
 * so auto-assign may change it again. The assignment (the person) is kept; only
 * its lifecycle state changes. Inverse of confirming one shift.
 */
export async function reopenShiftAssignment(shiftId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.staff_assignments)
    .update({ status: 'proposed', is_locked: false })
    .eq('shift_id', shiftId);
  if (error) throw error;
}

/**
 * Reopen every assignment for a programme's shifts (inverse of confirmAssignments):
 * back to 'proposed' and unlocked. The people stay assigned; only their state
 * changes, so a subsequent auto-assign run is free to reshuffle them.
 */
export async function reopenAssignments(programId: string): Promise<void> {
  const client = getClient();

  const { data: shiftRows, error: shiftError } = await client
    .from(TABLES.staff_shifts)
    .select('id')
    .eq('program_id', programId);
  if (shiftError) throw shiftError;

  const shiftIds = (shiftRows || []).map((r: any) => r.id);
  if (shiftIds.length === 0) return;

  const { error } = await client
    .from(TABLES.staff_assignments)
    .update({ status: 'proposed', is_locked: false })
    .in('shift_id', shiftIds);
  if (error) throw error;
}

/**
 * Remove assignments for a programme's shifts. The shifts themselves are kept,
 * so a subsequent auto-assign can refill them.
 *
 * By default this clears everything, including locked/confirmed picks. Pass
 * { onlyUnlocked: true } to preserve locked picks (manual assignments and
 * confirmed rosters) and clear only the unlocked, engine-proposed ones.
 */
export async function clearProgramAssignments(
  programId: string,
  options?: { onlyUnlocked?: boolean },
): Promise<void> {
  const client = getClient();

  const { data: shiftRows, error: shiftError } = await client
    .from(TABLES.staff_shifts)
    .select('id')
    .eq('program_id', programId);
  if (shiftError) throw shiftError;

  const shiftIds = (shiftRows || []).map((r: any) => r.id);
  if (shiftIds.length === 0) return;

  let query = client.from(TABLES.staff_assignments).delete().in('shift_id', shiftIds);
  if (options?.onlyUnlocked) query = query.eq('is_locked', false);

  const { error } = await query;
  if (error) throw error;
}
